/**
 * EODReportModal — sister-store / DM on-demand EOD recap viewer.
 *
 * Sister stores and the DM don't get the auto-email at 8pm anymore (user
 * feedback 2026-02: "EOD reports aren't needed for sister stores or DMs").
 * Instead, saving the 8pm checkpoint drops an "EOD report ready" bell
 * notification — tapping it opens this modal.
 *
 * Inside the modal:
 *   - The full HTML recap renders in a sandboxed-feeling card.
 *   - "Email me" button fires a one-shot send to the requesting manager's
 *     notification_email (does NOT blast the whole store).
 *   - "Save as PDF" uses the browser's native print pipeline so we don't
 *     need a server-side PDF renderer.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { X as XIcon, EnvelopeSimple, FilePdf } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EODReportModal({ date, storeId, user, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const printRef = useRef(null);

  // XSS-hardened HTML (2026-02-28 P0 security fix). Server-rendered
  // recap can include free-form fields (day notes, crew nicknames,
  // etc.). Anything that ever gets displayed as HTML has to go
  // through DOMPurify first — otherwise a stored `<script>` in a
  // day note becomes an admin-session-stealer for anyone who opens
  // the recap.
  //   • FORBID_TAGS: strip <script>, <style>, <iframe>, <object>,
  //     <embed>, <form>, <link>, <meta> outright.
  //   • FORBID_ATTR: kill inline event handlers (onclick, onerror,
  //     onload…) and `srcdoc` (iframe injection).
  //   • ALLOW_DATA_ATTR=false: block `data-*` payloads used by some
  //     XSS gadgets.
  //   • Config is memoized against `report?.html` so we don't
  //     re-sanitize on every render.
  const safeHtml = useMemo(() => {
    const raw = report?.html || "";
    if (!raw) return "";
    return DOMPurify.sanitize(raw, {
      FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "link", "meta"],
      FORBID_ATTR: [
        "onerror", "onload", "onclick", "onmouseover", "onmouseout",
        "onfocus", "onblur", "onchange", "oninput", "onsubmit",
        "onkeydown", "onkeyup", "onkeypress", "srcdoc", "formaction",
      ],
      ALLOW_DATA_ATTR: false,
    });
  }, [report?.html]);

  useEffect(() => {
    if (!date) return;
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/biz/eod-report/${date}`, { params: { store_id: storeId || "" } })
      .then((r) => {
        if (!alive) return;
        setReport(r.data);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Couldn't load EOD report.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [date, storeId]);

  const handleEmail = async () => {
    if (busy) return;
    setBusy("email");
    try {
      const r = await axios.post(`${API}/biz/eod-report/${date}/email`, {
        actor_id: user?.crew_id || "",
        store_id: storeId || "",
      });
      toast.success(`Emailed to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed.");
    } finally {
      setBusy("");
    }
  };

  const handlePdf = () => {
    if (!printRef.current) return;
    // Open the rendered HTML in a hidden window and trigger the browser's
    // native "Save as PDF" pipeline. Doesn't need a headless browser dep —
    // works on every modern desktop + iOS Safari (which exposes "Export as
    // PDF" from the print sheet).
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) {
      toast.error("Pop-up blocked — allow pop-ups for this site.");
      return;
    }
    w.document.write(`<!doctype html><html><head><title>EOD Report — ${report?.date_pretty || date}</title></head><body>${printRef.current.innerHTML}</body></html>`);
    w.document.close();
    // Defer to next tick so styles + images settle before the print dialog.
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch (e) {
        // no-op — user can still print manually
      }
    }, 250);
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center px-3 sm:px-6"
      onClick={onClose}
      data-testid="eod-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-100">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">EOD Recap</p>
            <h2 className="text-base font-black truncate text-slate-900">{report?.date_pretty || date}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100"
            aria-label="Close"
            data-testid="eod-modal-close"
          >
            <XIcon weight="bold" className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-slate-50">
          {loading ? (
            <div className="text-center py-12 text-slate-500 text-sm">Loading…</div>
          ) : report?.closed ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              Store was closed on this date ({report.reason}). No recap.
            </div>
          ) : (
            <div ref={printRef} className="px-2 py-3" dangerouslySetInnerHTML={{ __html: safeHtml }} />
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-white">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePdf}
            disabled={loading || !report || report.closed}
            data-testid="eod-modal-pdf"
            className="gap-1"
          >
            <FilePdf weight="bold" className="w-4 h-4" /> Save as PDF
          </Button>
          <Button
            size="sm"
            onClick={handleEmail}
            disabled={loading || !report || report.closed || busy === "email"}
            data-testid="eod-modal-email"
            className="gap-1 fb-btn-primary"
          >
            <EnvelopeSimple weight="bold" className="w-4 h-4" />
            {busy === "email" ? "Sending…" : "Email me"}
          </Button>
        </div>
      </div>
    </div>
  );
}
